import { useMemo, useSyncExternalStore } from "react";
import { breakpoints, mediaQueries, type ViewportClass } from "@tokens/index";

/**
 * Viewport class resolution.
 *
 * `useViewport()` subscribes to the four class media queries and the coarse
 * pointer query. Layout code reads the class; it never reads
 * `window.innerWidth`. When `matchMedia` is unavailable the hook reports
 * `expanded` with a fine pointer.
 */

export interface ViewportState {
    readonly viewport: ViewportClass;
    readonly isCompact: boolean;
    readonly isAtLeast: (v: ViewportClass) => boolean;
    readonly isAtMost: (v: ViewportClass) => boolean;
    readonly coarsePointer: boolean;
}

const ORDER: readonly ViewportClass[] = ["compact", "medium", "expanded", "wide"];
const FALLBACK: ViewportClass = "expanded";

/** Pure helper: resolve the class for a width in px. */
export function getViewportClass(width: number): ViewportClass {
    if (width >= breakpoints.wide) return "wide";
    if (width >= breakpoints.expanded) return "expanded";
    if (width >= breakpoints.medium) return "medium";
    return "compact";
}

function hasMatchMedia(): boolean {
    return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

const SUBSCRIBED_QUERIES = [
    mediaQueries.compact,
    mediaQueries.medium,
    mediaQueries.expanded,
    mediaQueries.wide,
    mediaQueries.coarsePointer,
] as const;

function subscribe(notify: () => void): () => void {
    if (!hasMatchMedia()) {
        return () => undefined;
    }
    const lists = SUBSCRIBED_QUERIES.map((query) => window.matchMedia(query));
    for (const list of lists) {
        list.addEventListener("change", notify);
    }
    return () => {
        for (const list of lists) {
            list.removeEventListener("change", notify);
        }
    };
}

// The snapshot is a string so `useSyncExternalStore` compares by value.
function getSnapshot(): string {
    if (!hasMatchMedia()) {
        return `${FALLBACK}|fine`;
    }
    const matched = ORDER.find((name) => window.matchMedia(mediaQueries[name]).matches) ?? FALLBACK;
    const coarse = window.matchMedia(mediaQueries.coarsePointer).matches;
    return `${matched}|${coarse ? "coarse" : "fine"}`;
}

function getServerSnapshot(): string {
    return `${FALLBACK}|fine`;
}

function parseSnapshot(snapshot: string): { viewport: ViewportClass; coarsePointer: boolean } {
    const [name, pointer] = snapshot.split("|");
    const viewport = ORDER.find((v) => v === name) ?? FALLBACK;
    return { viewport, coarsePointer: pointer === "coarse" };
}

export function useViewport(): ViewportState {
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    return useMemo<ViewportState>(() => {
        const { viewport, coarsePointer } = parseSnapshot(snapshot);
        const rank = ORDER.indexOf(viewport);
        return {
            viewport,
            isCompact: viewport === "compact",
            isAtLeast: (v) => rank >= ORDER.indexOf(v),
            isAtMost: (v) => rank <= ORDER.indexOf(v),
            coarsePointer,
        };
    }, [snapshot]);
}
